import React, { useEffect, useState } from "react";
import {
  XMarkIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import { useMutation, useQuery } from "react-query";
import ManageInstitutionTypesModal from "./ManageInstitutionTypeModal";
import toast from "react-hot-toast";

const AddInstitutionModal = ({ isOpen, onClose, onSuccess }) => {
  const [showInstitutionTypesModal, setShowInstitutionTypesModal] = useState(false);
  const [institutionTypesState, setInstitutionTypesState] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    institution_type_id: 0,
    address: "",
    contact_person: "",
    contact_number: "",
    email: "",
    status: "pending",
    structures: [
      {
        position: "",
        is_head: 0,
        member_name: "",
        member_contact: "",
        member_email: "",
        start_date: "",
        end_date: null,
      },
    ],
    remarks: "",
  });

  // Fetch institution types
  const { data: institutionTypes = [] } = useQuery(
    "institutionTypes",
    async () => {
      const res = await institutionAPI.getInstitutionTypes({ per_page: 100 });
      return res.data?.data?.institution_types || [];
    },
    {
      onError: (error) => {
        console.error("Error fetching institution types:", error);
        toast.error("Failed to load institution types");
      },
    }
  );

  // Sync fetched institution types into local state
  useEffect(() => {
    if (institutionTypes.length) {
      setInstitutionTypesState(institutionTypes);
    }
  }, [institutionTypes]);

  // Format date helper
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  // Create institution mutation
  const createInstitution = useMutation(
    async (institutionData) => {
      const response = await institutionAPI.createInstitution(institutionData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Institution created successfully");
        onSuccess();
      },
      onError: (error) => {
        const message =
          error?.response?.data?.message || error?.message || "Failed to create institution";
        toast.error(message);
      },
    }
  );

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.institution_type_id || !formData.address) {
      toast.error("Please fill all required fields");
      return;
    }

    // Prepare data
    const formattedData = {
      ...formData,
      structures: formData.structures.map((s) => ({
        ...s,
        start_date: s.start_date ? formatDate(s.start_date) : null,
        end_date: s.end_date ? formatDate(s.end_date) : null,
      })),
    };

    // Submit
    createInstitution.mutate(formattedData);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl overflow-hidden">
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Add New Institution</h2>
            <button onClick={onClose}>
              <XMarkIcon className="w-6 h-6 text-gray-600 hover:text-gray-800" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-8 overflow-y-auto max-h-[60vh]">
              {/* Institution Information */}
              <section>
                <h3 className="flex items-center text-md font-semibold mb-3">
                  <BuildingOfficeIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Institution Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">
                      Institution Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter institution name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2 mt-1"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-sm font-medium">
                        Institution Type <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowInstitutionTypesModal(true)}
                        className="text-xs bg-[#DCEBFE] text-blue-600 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                      >
                        + New Institution Type
                      </button>
                    </div>
                    <select
                      value={formData.institution_type_id}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          institution_type_id: Number(e.target.value),
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="">Select institution type</option>
                      {institutionTypesState.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter complete address"
                      value={formData.address}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, address: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2 mt-1"
                    />
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section>
                <h3 className="flex items-center text-md font-semibold mb-3">
                  <PhoneIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Contact Person</label>
                    <input
                      type="text"
                      placeholder="Full name of contact person"
                      value={formData.contact_person}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, contact_person: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Contact Number</label>
                    <input
                      type="text"
                      placeholder="+63 926 354 7586"
                      value={formData.contact_number}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, contact_number: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email Address</label>
                    <input
                      type="email"
                      placeholder="example@email.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                </div>
              </section>

              {/* Head of Institution */}
              <section>
                <h3 className="flex items-center text-md font-semibold mb-3">
                  <UserIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Head of Institution
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Member Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Dr. Maria Santos"
                      value={formData.structures[0].member_name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], member_name: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Position</label>
                    <input
                      type="text"
                      placeholder="e.g., School Principal"
                      value={formData.structures[0].position}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], position: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Member Contact</label>
                    <input
                      type="text"
                      placeholder="+63 926 354 7586"
                      value={formData.structures[0].member_contact}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], member_contact: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Member Email</label>
                    <input
                      type="email"
                      placeholder="example@email.com"
                      value={formData.structures[0].member_email}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], member_email: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <input
                      type="date"
                      value={formData.structures[0].start_date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], start_date: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Date</label>
                    <input
                      type="date"
                      value={formData.structures[0].end_date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          structures: [{ ...prev.structures[0], end_date: e.target.value }],
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2"
                    />
                  </div>
                </div>
              </section>

              {/* Additional Information */}
              <section>
                <h3 className="flex items-center text-md font-semibold mb-3">
                  <DocumentTextIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Additional Information
                </h3>
                <textarea
                  rows={3}
                  placeholder="Please enter remarks related to this institution."
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, remarks: e.target.value }))
                  }
                  className="w-full border rounded-md px-3 py-2"
                />
              </section>

              {/* Operational Status */}
              <section>
                <h3 className="flex items-center text-md font-semibold mb-3">
                  <CheckCircleIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Operational Status
                </h3>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        status: prev.status === "active" ? "inactive" : "active",
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      formData.status === "active" ? "bg-green-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.status === "active" ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-sm font-medium">
                    {formData.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </section>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createInstitution.isLoading}
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {createInstitution.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Create Institution"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Manage Institution Types Modal */}
      <ManageInstitutionTypesModal
        isOpen={showInstitutionTypesModal}
        onClose={() => setShowInstitutionTypesModal(false)}
        onInstitutionTypeAdded={(newTypeObj) => {
          setInstitutionTypesState((prev) => [...prev, newTypeObj]);
          setFormData((prev) => ({ ...prev, institution_type_id: newTypeObj.id }));
        }}
      />
    </>
  );
};

export default AddInstitutionModal;
